import {lazy, Suspense, useEffect} from "react";
import {Navigate, Route, Routes, useLocation, useSearchParams} from "react-router-dom";
import {Toaster} from "sonner";
import {AuthProvider} from "./context/AuthContext";
import BusinessLayout from "./layouts/BusinessLayout.tsx";
import AppLayout from "./layouts/AppLayout.tsx";
import AdminLayout from "./layouts/AdminLayout.tsx";
import {BusinessProvider} from "./context/BusinessContext.tsx";
import {RequireAuth, RequireBusinessPermission, RequireRoles, ROLES} from "./routing/guards";
import {PERMISSIONS} from "./lib/roles";
import CookieConsentBanner from "./components/CookieConsentBanner.tsx";
// Manage-event wizard layout is needed eagerly to host the step routes
import ManageEventLayout from "./features/business/features/events/ManageEventLayout.tsx";
import {DetailsStep} from "./features/business/features/events/steps/DetailsStep.tsx";
import {ImagesStep} from "./features/business/features/events/steps/ImagesStep.tsx";
import {SeatMapStep} from "./features/business/features/events/steps/SeatMapStep.tsx";
import {TicketsStep} from "./features/business/features/events/steps/TicketsStep.tsx";
import {DataCollectionStep} from "./features/business/features/events/steps/DataCollectionStep.tsx";
import {RefundPolicyStep} from "./features/business/features/events/steps/RefundPolicyStep.tsx";
import {MailingStep} from "./features/business/features/events/steps/MailingStep.tsx";
import {ReviewPublishStep} from "./features/business/features/events/steps/ReviewPublishStep.tsx";
import {SponsorsStep} from "./features/business/features/events/steps/SponsorsStep.tsx";
import {SpeakersStep} from "./features/business/features/events/steps/SpeakersStep.tsx";
import {RemindersStep} from "./features/business/features/events/steps/RemindersStep.tsx";
import {CouponsStep} from "./features/business/features/events/steps/CouponsStep.tsx";
import {PromotersStep} from "./features/business/features/events/steps/PromotersStep.tsx";
import {FeedbackStep} from "./features/business/features/events/steps/FeedbackStep.tsx";
import {EventDaysStep} from "./features/business/features/events/steps/EventDaysStep.tsx";
import {OptionsStep} from "./features/business/features/events/steps/OptionsStep.tsx";
import ManageTournamentLayout from "./features/business/features/tournaments/ManageTournamentLayout.tsx";
import {DetailsStep as TournamentDetailsStep} from "./features/business/features/tournaments/steps/DetailsStep.tsx";
import {ImagesStep as TournamentImagesStep} from "./features/business/features/tournaments/steps/ImagesStep.tsx";
import {
    PlayCategoriesStep as TournamentPlayCategoriesStep
} from "./features/business/features/tournaments/steps/PlayCategoriesStep.tsx";
import {
    DataCollectionStep as TournamentDataCollectionStep
} from "./features/business/features/tournaments/steps/DataCollectionStep.tsx";
import {
    ReviewPublishStep as TournamentReviewPublishStep
} from "./features/business/features/tournaments/steps/ReviewPublishStep.tsx";

// Public pages
const HomePage = lazy(() => import("./features/home/HomePage"));
const EventsPage = lazy(() => import("./features/events/EventsPage"));
const CalendarPage = lazy(() => import("./features/calendar/CalendarPage"));
const AboutPage = lazy(() => import("./features/about/AboutPage"));
const CashlessPage = lazy(() => import("./features/cashless/CashlessPage.tsx"));
const VendorSpecialsBoardPage = lazy(() => import("./features/cashless/VendorSpecialsBoardPage.tsx"));
const MembershipsPage = lazy(() => import("./features/memberships/MembershipProgramsPage.tsx"));
const MembershipJoinPage = lazy(() => import("./features/memberships/MembershipJoinPage.tsx"));
const EventDetailsPage = lazy(() => import("./features/event-details/EventDetailsPage"));
const PublicTournamentsPage = lazy(() => import("./features/tournaments/PublicTournamentsPage.tsx"));
const PublicTournamentDetailsPage = lazy(() => import("./features/tournaments/PublicTournamentDetailsPage.tsx"));
const PublicTournamentCompetitionPage = lazy(() => import("./features/tournaments/PublicTournamentCompetitionPage.tsx"));
const HostEventsPage = lazy(() => import("./features/events/HostEventsPage.tsx"));
const CheckoutStatusPage = lazy(() => import("./features/checkout/CheckoutStatusPage"));
const PayByCardPage = lazy(() => import("./features/checkout/PayByCardPage.tsx"));
const FeedbackPage = lazy(() => import("./features/feedback/FeedbackPage.tsx"));
const PrivacyPolicyPage = lazy(() => import("./features/legal/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./features/legal/TermsPage"));
const HelpCenterPage = lazy(() => import("./features/help/HelpCenterPage.tsx"));
const HelpCategoryPage = lazy(() => import("./features/help/HelpCategoryPage.tsx"));
const HelpArticlePage = lazy(() => import("./features/help/HelpArticlePage.tsx"));
const WalletPayoutRequestPage = lazy(() => import("./features/cashless/WalletPayoutRequestPage.tsx"));

// Auth
const LoginPage = lazy(() => import("./features/auth/LoginPage"));
const SignUpPage = lazy(() => import("./features/auth/SignUpPage"));
const ForgotPasswordPage = lazy(() => import("./features/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./features/auth/ResetPasswordPage"));
const OAuthCallbackPage = lazy(() => import("./features/auth/OAuthCallbackPage"));
const BecomePartnerPage = lazy(() => import("./features/auth/PartnerSignUpPage.tsx"));

// User profile
const ProfilePage = lazy(() => import("./features/profile/ProfilePage.tsx"));
const MyTicketsPage = lazy(() => import("./features/profile/MyTicketsPage.tsx"));
const UserMembershipsPage = lazy(() => import("./features/profile/UserMembershipsPage.tsx"));
const MyTournamentsPage = lazy(() => import("./features/profile/MyTournamentsPage.tsx"));
const MyPromotionsPage = lazy(() => import("./features/profile/MyPromotionsPage.tsx"));
const MyAgentEarningsPage = lazy(() => import("./features/profile/MyAgentEarningsPage.tsx"));
const SavedEventsPage = lazy(() => import("./features/profile/SavedEventsPage.tsx"));
const WalletPage = lazy(() => import("./features/profile/WalletPage.tsx"));

// Business
const BusinessDashboardPage = lazy(() => import("./features/business/features/dashboard/BusinessDashboardPage.tsx"));
const BusinessProfilePage = lazy(() => import("./features/business/features/profile/BusinessProfilePage.tsx"));
const BusinessEventsPage = lazy(() => import("./features/business/features/events/BusinessEventsPage.tsx"));
const BusinessOrdersPage = lazy(() => import("./features/business/features/orders/BusinessOrdersPage.tsx"));
const BusinessCustomersPage = lazy(() => import("./features/business/features/customers/BusinessCustomersPage.tsx"));
const BusinessCustomerDetailPage = lazy(() => import("./features/business/features/customers/BusinessCustomerDetailPage.tsx"));
const CashlessVendorsPage = lazy(() => import("./features/business/features/cashless/CashlessVendorsPage.tsx"));
const MembershipsProgramsPage = lazy(() => import("./features/business/features/memberships/MembershipsProgramsPage.tsx"));
const BusinessMailingPage = lazy(() => import("./features/business/features/mailing/BusinessMailingPage.tsx"));
const BusinessMailingDetailPage = lazy(() => import("./features/business/features/mailing/BusinessMailingDetailPage.tsx"));
const MailingBrandingPage = lazy(() => import("./features/business/features/mailing/MailingBrandingPage.tsx"));
const LuckyDrawPage = lazy(() => import("./features/business/features/lucky-draw/LuckyDrawPage.tsx"));
const LuckyDrawDetailPage = lazy(() => import("./features/business/features/lucky-draw/LuckyDrawDetailPage.tsx"));
const BusinessUsersPage = lazy(() => import("./features/business/features/users/BusinessUsersPage.tsx"));
const CashlessStockPage = lazy(() => import("./features/business/features/cashless/CashlessStockPage.tsx"));
const CashlessCategoriesPage = lazy(() => import("./features/business/features/cashless/CashlessCategoriesPage.tsx"));
const CashlessSpecialsPage = lazy(() => import("./features/business/features/cashless/CashlessSpecialsPage.tsx"));
const CashlessTransactionsDashboardPage = lazy(() => import("./features/business/features/cashless/CashlessTransactionsDashboardPage.tsx"));
const CashlessStockDashboardPage = lazy(() => import("./features/business/features/cashless/CashlessStockDashboardPage.tsx"));
const CreateMembershipsPage = lazy(() => import("./features/business/features/memberships/CreateMembershipsPage.tsx"));
const MembershipProgramDetailPage = lazy(() => import("./features/business/features/memberships/MembershipProgramDetailPage.tsx"));
const MembershipMembersPage = lazy(() => import("./features/business/features/memberships/MembershipMembersPage.tsx"));
const MembershipStatisticsPage = lazy(() => import("./features/business/features/memberships/MembershipStatisticsPage.tsx"));
const MembershipMemberDetailPage = lazy(() => import("./features/business/features/memberships/MembershipMemberDetailPage.tsx"));
const BusinessSelectPage = lazy(() => import("./features/business/features/BusinessSelectPage.tsx"));
const BusinessBrandingPage = lazy(() => import("./features/business/features/profile/BusinessBrandingPage.tsx"));
const BusinessSocialMediaPage = lazy(() => import("./features/business/features/profile/BusinessSocialMediaPage.tsx"));
const BusinessMetaPixelPage = lazy(() => import("./features/business/features/profile/BusinessMetaPixelPage.tsx"));
const BusinessEventOrdersPage = lazy(() => import("./features/business/features/orders/BusinessEventOrdersPage.tsx"));
const BusinessFeedbackPage = lazy(() => import("./features/business/features/feedback/BusinessFeedbackPage.tsx"));
const BusinessManualOrderPage = lazy(() => import("./features/business/features/orders/BusinessManualOrderPage.tsx"));
const BusinessBankingDetailsPage = lazy(() => import("./features/business/features/profile/BusinessBankingDetailsPage.tsx"));
const BusinessInvoiceEmailsPage = lazy(() => import("./features/business/features/profile/BusinessInvoiceEmailsPage.tsx"));
const BusinessEventFeedbackPage = lazy(() => import("./features/business/features/feedback/BusinessEventFeedbackPage.tsx"));
const BusinessWelcomePage = lazy(() => import("./features/business/features/welcome/BusinessWelcomePage.tsx"));
const BusinessDownloadsPage = lazy(() => import("./features/business/features/downloads/BusinessDownloadsPage.tsx"));

// Tournaments
const BusinessTournamentsPage = lazy(() => import("./features/business/features/tournaments/BusinessTournamentsPage.tsx"));
const TournamentCompetitorsPage = lazy(() => import("./features/business/features/tournaments/TournamentCompetitorsPage.tsx"));
const TournamentCompetitionPage = lazy(() => import("./features/business/features/tournaments/TournamentCompetitionPage.tsx"));

// Admin
const AdminDashboardPage = lazy(() => import("./features/admin/AdminDashboardPage.tsx"));
const AdminCashlessDashboardPage = lazy(() => import("./features/admin/AdminCashlessDashboardPage.tsx"));
const AdminTopUpExportsPage = lazy(() => import("./features/admin/AdminTopUpExportsPage.tsx"));
const AdminPayoutRequestsPage = lazy(() => import("./features/admin/AdminPayoutRequestsPage.tsx"));
const AdminCardRecoveryPage = lazy(() => import("./features/admin/AdminCardRecoveryPage.tsx"));
const AdminFailedPaymentsPage = lazy(() => import("./features/admin/AdminFailedPaymentsPage.tsx"));
const AdminCardPayLinkPage = lazy(() => import("./features/admin/AdminCardPayLinkPage.tsx"));
const AdminBannersPage = lazy(() => import("./features/admin/AdminBannersPage.tsx"));
const AdminSeatMapsPage = lazy(() => import("./features/admin/AdminSeatMapsPage.tsx"));
const AdminDuplicateSeatsPage = lazy(() => import("./features/admin/AdminDuplicateSeatsPage.tsx"));
const AdminTicketCorrectionsPage = lazy(() => import("./features/admin/AdminTicketCorrectionsPage.tsx"));
const AdminSeatingOverviewPage = lazy(() => import("./features/admin/AdminSeatingOverviewPage.tsx"));
const AdminBusinessesPage = lazy(() => import("./features/admin/AdminBusinessesPage.tsx"));
const AdminBusinessDetailPage = lazy(() => import("./features/admin/AdminBusinessDetailPage.tsx"));
const AdminEventPayoutsPage = lazy(() => import("./features/admin/AdminEventPayoutsPage.tsx"));
const AdminEventPayoutGroupsPage = lazy(() => import("./features/admin/AdminEventPayoutGroupsPage.tsx"));
const AdminOperationsPage = lazy(() => import("./features/admin/AdminOperationsPage.tsx"));
const AdminEventPlanningPage = lazy(() => import("./features/admin/AdminEventPlanningPage.tsx"));
const AdminEventPayoutGroupPage = lazy(() => import("./features/admin/AdminEventPayoutGroupPage.tsx"));
const AdminSalesAgentsPage = lazy(() => import("./features/admin/AdminSalesAgentsPage.tsx"));
const AdminSalesAgentDetailPage = lazy(() => import("./features/admin/AdminSalesAgentDetailPage.tsx"));
const AdminPartnersPage = lazy(() => import("./features/admin/AdminPartnersPage.tsx"));
const AdminPartnerDetailPage = lazy(() => import("./features/admin/AdminPartnerDetailPage.tsx"));
const AdminEventDiscoveryPage = lazy(() => import("./features/admin/AdminEventDiscoveryPage.tsx"));
const AdminEmployeeAccountsPage = lazy(() => import("./features/admin/AdminEmployeeAccountsPage.tsx"));
const AdminNewsletterPage = lazy(() => import("./features/admin/AdminNewsletterPage.tsx"));
const AdminNewsletterCampaignPage = lazy(() => import("./features/admin/AdminNewsletterCampaignPage.tsx"));
const AdminEmailFailuresPage = lazy(() => import("./features/admin/AdminEmailFailuresPage.tsx"));

// Newsletter (public)
const NewsletterUnsubscribePage = lazy(() => import("./features/newsletter/NewsletterUnsubscribePage.tsx"));

// Business mailing (public)
const MailingUnsubscribePage = lazy(() => import("./features/mailing/MailingUnsubscribePage.tsx"));

function RouteFallback() {
    return (
        <div className="flex min-h-[60vh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-orange-500"/>
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BusinessProvider>
                <ScrollToTop/>
                <Suspense fallback={<RouteFallback/>}>
                    <Routes>
                        {/* AUTH callback (public, no layout) */}
                        <Route path="/oauth/callback" element={<OAuthCallbackPage/>}/>

                        {/* Public vendor specials display board (guest screens, no layout) */}
                        <Route path="/specials/:vendorId" element={<VendorSpecialsBoardPage/>}/>

                        {/* PUBLIC LAYOUT */}
                        <Route element={<AppLayout transparentNavbar/>}>
                            <Route index element={<HomePage/>}/>
                        </Route>

                        {/* DEFAULT LAYOUT (mixed public + protected pages) */}
                        <Route element={<AppLayout invertNavbar/>}>
                            <Route path="explore" element={<EventsPage/>}/>
                            <Route path="calendar" element={<CalendarPage/>}/>
                            <Route path="about-us" element={<AboutPage/>}/>
                            <Route path="cashless" element={<CashlessPage/>}/>
                            <Route path="account/payout" element={<WalletPayoutRequestPage/>}/>
                            <Route path="memberships" element={<MembershipsPage/>}/>
                            <Route path="memberships/:programId" element={<MembershipJoinPage/>}/>

                            <Route path="login" element={<LoginPage/>}/>
                            <Route path="signup" element={<SignUpPage/>}/>
                            <Route path="forgot-password" element={<ForgotPasswordPage/>}/>
                            <Route path="reset-password" element={<ResetPasswordPage/>}/>
                            <Route path="become-a-partner" element={<BecomePartnerPage/>}/>

                            {/* public pages that still use DefaultLayout */}
                            <Route path="event" element={<EventIdRedirect/>}/>
                            <Route path="explore/:slug" element={<EventDetailsPage/>}/>
                            <Route path="tournaments" element={<PublicTournamentsPage/>}/>
                            <Route path="tournaments/:slug" element={<PublicTournamentDetailsPage/>}/>
                            <Route path="tournaments/:slug/competition" element={<PublicTournamentCompetitionPage/>}/>
                            <Route path="host/:hostSlug">
                                <Route index element={<HostEventsPage/>}/>
                                <Route path=":slug" element={<EventDetailsPage/>}/>
                            </Route>
                            <Route path="checkout/status/:reference" element={<CheckoutStatusPage/>}/>
                            {/* Linked from every company invoice email, opened with no session and carrying its
                                own ?token= authorisation */}
                            <Route path="pay/:reference" element={<PayByCardPage/>}/>
                            <Route path="feedback/:token" element={<FeedbackPage/>}/>
                            {/* Linked from every newsletter, opened with no session from an email client */}
                            <Route path="newsletter/unsubscribe" element={<NewsletterUnsubscribePage/>}/>
                            {/* Linked from every mailing a company sends, opened with no session. The
                                path is what BusinessMailingSendWorker builds the link from, so the two
                                have to agree. */}
                            <Route path="mailings/unsubscribe" element={<MailingUnsubscribePage/>}/>
                            <Route path="feedback/preview/:businessId/:eventId" element={<FeedbackPage preview/>}/>
                            <Route path="legal/privacy-policy" element={<PrivacyPolicyPage/>}/>
                            <Route path="legal/terms-and-conditions" element={<TermsPage/>}/>
                            <Route path="help" element={<HelpCenterPage/>}/>
                            <Route path="help/:categorySlug" element={<HelpCategoryPage/>}/>
                            <Route path="help/:categorySlug/:articleSlug" element={<HelpArticlePage/>}/>
                            <Route path="saved" element={<SavedEventsPage/>}/>

                            {/* authenticated-only */}
                            <Route element={<RequireAuth/>}>
                                <Route path="profile" element={<ProfilePage/>}/>
                                <Route path="profile/tickets" element={<MyTicketsPage/>}/>
                                <Route path="profile/memberships" element={<UserMembershipsPage/>}/>
                                <Route path="profile/tournaments" element={<MyTournamentsPage/>}/>
                                <Route path="profile/promotions" element={<MyPromotionsPage/>}/>
                                <Route path="profile/sales-agent" element={<MyAgentEarningsPage/>}/>
                                <Route path="profile/account" element={<WalletPage/>}/>
                            </Route>
                        </Route>

                        {/* BUSINESS (role-protected) */}
                        <Route
                            element={<RequireRoles roles={[ROLES.BUSINESS, ROLES.ADMIN, ROLES.EMPLOYEE]} fallbackTo="/"/>}>
                            <Route path="/business" element={<BusinessLayout/>}>
                                <Route path="select" element={<BusinessSelectPage/>}/>

                                {/* Business admins, or employees granted SALES at this business */}
                                <Route element={<RequireBusinessPermission permissions={[PERMISSIONS.SALES]}
                                                                           fallbackTo="/"/>}>
                                    <Route path="dashboard" element={<BusinessDashboardPage/>}/>
                                    <Route path="orders" element={<BusinessOrdersPage/>}/>
                                    <Route path="orders/events/:eventId" element={<BusinessEventOrdersPage/>}/>
                                    <Route path="orders/events/:eventId/manual"
                                           element={<BusinessManualOrderPage/>}/>
                                </Route>

                                {/* BUSINESS_ADMIN + ADMINISTRATOR only */}
                                <Route element={<RequireRoles roles={[ROLES.BUSINESS, ROLES.ADMIN]}
                                                              fallbackTo="/business/dashboard"/>}>
                                    <Route path="welcome" element={<BusinessWelcomePage/>}/>
                                    <Route path="profile/details" element={<BusinessProfilePage/>}/>
                                    <Route path="profile/branding" element={<BusinessBrandingPage/>}/>
                                    <Route path="profile/social-media" element={<BusinessSocialMediaPage/>}/>
                                    <Route path="profile/meta-pixel" element={<BusinessMetaPixelPage/>}/>
                                    <Route path="profile/banking" element={<BusinessBankingDetailsPage/>}/>
                                    <Route path="profile/invoice-emails" element={<BusinessInvoiceEmailsPage/>}/>
                                    <Route path="downloads" element={<BusinessDownloadsPage/>}/>

                                    <Route path="events" element={<BusinessEventsPage/>}/>
                                    <Route path="events/:eventId/feedback-responses"
                                           element={<BusinessEventFeedbackPage/>}/>
                                    <Route path="feedback" element={<BusinessFeedbackPage/>}/>
                                    <Route path="/business/events/new" element={<ManageEventLayout/>}>
                                        <Route index element={<Navigate to="details" replace/>}/>
                                        <Route path="details" element={<DetailsStep mode="create"/>}/>
                                        <Route path="event-days" element={<EventDaysStep mode="create"/>}/>
                                        <Route path="images" element={<ImagesStep mode="create"/>}/>
                                        <Route path="seatmap" element={<SeatMapStep mode="create"/>}/>
                                        <Route path="tickets" element={<TicketsStep mode="create"/>}/>
                                        <Route path="data-collection" element={<DataCollectionStep mode="create"/>}/>
                                        <Route path="coupons" element={<CouponsStep mode="create"/>}/>
                                        <Route path="promoters" element={<PromotersStep mode="create"/>}/>
                                        <Route path="refund-policy" element={<RefundPolicyStep mode="create"/>}/>
                                        <Route path="mailing" element={<MailingStep mode={"create"}/>}/>
                                        <Route path="reminders" element={<RemindersStep mode={"create"}/>}/>
                                        <Route path="sponsors" element={<SponsorsStep mode={"create"}/>}/>
                                        <Route path="speakers" element={<SpeakersStep mode={"create"}/>}/>
                                        <Route path="feedback" element={<FeedbackStep mode={"create"}/>}/>
                                        <Route path="options" element={<OptionsStep mode="create"/>}/>
                                        <Route path="review" element={<ReviewPublishStep mode={"create"}/>}/>
                                    </Route>

                                    <Route path="/business/events/:eventId/edit" element={<ManageEventLayout/>}>
                                        <Route index element={<Navigate to="details" replace/>}/>
                                        <Route path="details" element={<DetailsStep mode="edit"/>}/>
                                        <Route path="event-days" element={<EventDaysStep mode="edit"/>}/>
                                        <Route path="images" element={<ImagesStep mode="edit"/>}/>
                                        <Route path="seatmap" element={<SeatMapStep mode="edit"/>}/>
                                        <Route path="tickets" element={<TicketsStep mode="edit"/>}/>
                                        <Route path="data-collection" element={<DataCollectionStep mode="edit"/>}/>
                                        <Route path="coupons" element={<CouponsStep mode="edit"/>}/>
                                        <Route path="promoters" element={<PromotersStep mode="edit"/>}/>
                                        <Route path="refund-policy" element={<RefundPolicyStep mode="edit"/>}/>
                                        <Route path="mailing" element={<MailingStep mode={"edit"}/>}/>
                                        <Route path="reminders" element={<RemindersStep mode={"edit"}/>}/>
                                        <Route path="sponsors" element={<SponsorsStep mode={"edit"}/>}/>
                                        <Route path="speakers" element={<SpeakersStep mode={"edit"}/>}/>
                                        <Route path="feedback" element={<FeedbackStep mode={"edit"}/>}/>
                                        <Route path="options" element={<OptionsStep mode="edit"/>}/>
                                        <Route path="review" element={<ReviewPublishStep mode={"edit"}/>}/>
                                    </Route>

                                    <Route path="tournaments" element={<BusinessTournamentsPage/>}/>
                                    <Route path="tournaments/:tournamentId/competitors"
                                           element={<TournamentCompetitorsPage/>}/>
                                    <Route path="tournaments/:tournamentId/competition"
                                           element={<TournamentCompetitionPage/>}/>
                                    <Route path="/business/tournaments/:tournamentId/edit"
                                           element={<ManageTournamentLayout/>}>
                                        <Route index element={<Navigate to="details" replace/>}/>
                                        <Route path="details" element={<TournamentDetailsStep mode="edit"/>}/>
                                        <Route path="images" element={<TournamentImagesStep mode="edit"/>}/>
                                        <Route path="play-categories"
                                               element={<TournamentPlayCategoriesStep mode="edit"/>}/>
                                        <Route path="data-collection"
                                               element={<TournamentDataCollectionStep mode="edit"/>}/>
                                        <Route path="review" element={<TournamentReviewPublishStep mode="edit"/>}/>
                                    </Route>

                                    <Route path="customers" element={<BusinessCustomersPage/>}/>
                                    <Route path="customers/:type/:customerId" element={<BusinessCustomerDetailPage/>}/>

                                    <Route path="cashless/dashboard" element={<CashlessStockDashboardPage/>}/>
                                    <Route path="cashless/vendors" element={<CashlessVendorsPage/>}/>
                                    <Route path="cashless/stock-items" element={<CashlessStockPage/>}/>
                                    <Route path="cashless/menu-categories" element={<CashlessCategoriesPage/>}/>
                                    <Route path="cashless/specials" element={<CashlessSpecialsPage/>}/>
                                    <Route path="cashless/transactions" element={<CashlessTransactionsDashboardPage/>}/>

                                    <Route path="memberships/programs" element={<MembershipsProgramsPage/>}/>
                                    <Route path="memberships/statistics" element={<MembershipStatisticsPage/>}/>
                                    <Route path="memberships/members" element={<MembershipMembersPage/>}/>
                                    <Route path="memberships/members/:membershipId"
                                           element={<MembershipMemberDetailPage/>}/>
                                    <Route path="memberships/new" element={<CreateMembershipsPage/>}/>
                                    <Route path="memberships/:programId" element={<MembershipProgramDetailPage/>}/>

                                    <Route path="mailing" element={<BusinessMailingPage/>}/>
                                    {/* Ahead of the :mailingId route, which would otherwise match it */}
                                    <Route path="mailing/branding" element={<MailingBrandingPage/>}/>
                                    <Route path="mailing/:mailingId" element={<BusinessMailingDetailPage/>}/>
                                    <Route path="lucky-draws" element={<LuckyDrawPage/>}/>
                                    <Route path="lucky-draws/:drawId" element={<LuckyDrawDetailPage/>}/>
                                    <Route path="users" element={<BusinessUsersPage/>}/>
                                </Route>

                                <Route index={true} element={<Navigate to="dashboard" replace/>}/>

                            </Route>
                        </Route>


                        {/*ADMIN (role-protected) */}
                        <Route element={<RequireRoles roles={[ROLES.ADMIN]} fallbackTo="/"/>}>
                            <Route path="/admin" element={<AdminLayout/>}>
                                <Route path="dashboard" element={<AdminDashboardPage/>}/>
                                <Route path="cashless" element={<AdminCashlessDashboardPage/>}/>
                                <Route path="cashless/topup-exports" element={<AdminTopUpExportsPage/>}/>
                                <Route path="payouts" element={<AdminPayoutRequestsPage/>}/>
                                <Route path="card-recovery" element={<AdminCardRecoveryPage/>}/>
                                <Route path="failed-payments" element={<AdminFailedPaymentsPage/>}/>
                                <Route path="card-pay-links" element={<AdminCardPayLinkPage/>}/>
                                <Route path="banners" element={<AdminBannersPage/>}/>
                                <Route path="seatmaps" element={<AdminSeatMapsPage/>}/>
                                <Route path="seating" element={<AdminDuplicateSeatsPage/>}/>
                                <Route path="seating/events/:eventId" element={<AdminSeatingOverviewPage/>}/>
                                <Route path="ticket-corrections" element={<AdminTicketCorrectionsPage/>}/>
                                <Route path="businesses" element={<AdminBusinessesPage/>}/>
                                <Route path="businesses/:id" element={<AdminBusinessDetailPage/>}/>
                                <Route path="events/:eventId/payouts" element={<AdminEventPayoutsPage/>}/>
                                <Route path="operations" element={<AdminOperationsPage/>}/>
                                <Route path="events/:eventId/planning" element={<AdminEventPlanningPage/>}/>
                                <Route path="payout-groups" element={<AdminEventPayoutGroupsPage/>}/>
                                <Route path="payout-groups/:groupId" element={<AdminEventPayoutGroupPage/>}/>
                                <Route path="sales-agents" element={<AdminSalesAgentsPage/>}/>
                                <Route path="sales-agents/:agentId" element={<AdminSalesAgentDetailPage/>}/>
                                <Route path="partners" element={<AdminPartnersPage/>}/>
                                <Route path="partners/:partnerId" element={<AdminPartnerDetailPage/>}/>
                                <Route path="employee-accounts" element={<AdminEmployeeAccountsPage/>}/>
                                <Route path="discovery" element={<AdminEventDiscoveryPage/>}/>
                                <Route path="newsletter" element={<AdminNewsletterPage/>}/>
                                <Route path="newsletter/:campaignId" element={<AdminNewsletterCampaignPage/>}/>
                                <Route path="email-failures" element={<AdminEmailFailuresPage/>}/>
                            </Route>
                        </Route>

                        {/* Catch-all */}
                        <Route path="*" element={<Navigate to="/" replace/>}/>
                    </Routes>
                </Suspense>

                <Toaster position="top-right" closeButton expand/>
                <CookieConsentBanner/>
            </BusinessProvider>
        </AuthProvider>
    );
}

function ScrollToTop() {
    const {pathname, hash} = useLocation();

    useEffect(() => {
        // Let in-page anchor links (#section) scroll to their target instead.
        if (hash) return;
        window.scrollTo(0, 0);
    }, [pathname, hash]);

    return null;
}

function EventIdRedirect() {
    const [sp] = useSearchParams();
    const id = sp.get("id");
    return id ? <Navigate to={`/explore/${id}`} replace/> : <Navigate to="/" replace/>;
}
